/**
 * Viewer mode - readonly graph gallery
 */
import { EditorView, basicSetup } from "codemirror";
import { EditorState } from "@codemirror/state";
import { yaml } from "@codemirror/lang-yaml";
import { oneDark } from "@codemirror/theme-one-dark";
import { EditorView as EditorViewExt } from "@codemirror/view";

import { GraphRenderer } from "./graph.js";
import { exportJSON, importJSON } from "./api.js";
import {
  showNodeDetails,
  hideNodeDetails,
} from "./ui.js";
import {
  NavigationManager,
  renderBreadcrumbs,
  renderMinimap,
  highlightNavigationPath,
} from "./navigation.js";
import { setupResizer } from "./resizer.js";

// Global state
let currentGraphData = null;
let originalBackendData = null;
let graphRenderer = null;
let navigationManager = null;
let editorView = null;

// Initialize viewer
async function init() {
  // Initialize navigation manager
  navigationManager = new NavigationManager();
  navigationManager.onNavigate = handleNavigation;

  // Initialize readonly CodeMirror editor
  setupReadonlyEditor();

  // Initialize graph renderer
  const svgElement = document.getElementById("graph-svg");
  graphRenderer = new GraphRenderer(svgElement);

  // Set up node click handler
  graphRenderer.onNodeClick = handleNodeClick;

  // Set up drill-down handler
  graphRenderer.onNodeDrillDown = handleNodeDrillDown;

  // Load graph gallery
  await loadGraphGallery();

  // Listen for hash changes (browser back/forward buttons)
  window.addEventListener('hashchange', handleHashChange);

  // Set up panel resizer
  setupResizer({
    onResize: () => {
      if (navigationManager && navigationManager.hierarchyData) {
        const graphData = navigationManager.getCurrentGraph();
        graphRenderer.render(graphData);
      } else if (currentGraphData) {
        graphRenderer.render(currentGraphData);
      }
      graphRenderer.resetView();
    }
  });

  // Set up event listeners
  setupEventListeners();

  // Handle window resize
  let resizeTimeout;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const svg = document.getElementById("graph-svg");
      graphRenderer.width = svg.clientWidth;
      graphRenderer.height = svg.clientHeight;

      if (navigationManager && navigationManager.hierarchyData) {
        const graphData = navigationManager.getCurrentGraph();
        graphRenderer.render(graphData);
      } else if (currentGraphData) {
        graphRenderer.render(currentGraphData);
      }

      graphRenderer.resetView();
    }, 250);
  });
}

function setupReadonlyEditor() {
  const textarea = document.getElementById("text-input");
  const parent = textarea.parentElement;

  const startState = EditorState.create({
    doc: textarea.value || "",
    extensions: [
      basicSetup,
      yaml(),
      oneDark,
      EditorViewExt.lineWrapping,
      EditorState.readOnly.of(true), // Make it readonly
      EditorViewExt.theme({
        "&": {
          height: "100%",
          fontSize: "0.95rem",
          fontFamily: "'Fira Code', 'Courier New', monospace",
        },
        ".cm-scroller": {
          overflow: "auto",
          minHeight: "250px",
        },
        ".cm-content": {
          padding: "1rem",
        },
      }),
    ],
  });

  const editorContainer = document.createElement("div");
  editorContainer.id = "codemirror-container";

  parent.insertBefore(editorContainer, textarea);

  editorView = new EditorView({
    state: startState,
    parent: editorContainer,
  });

  textarea.style.display = "none";
}

function setEditorText(text) {
  if (!editorView) return;
  editorView.dispatch({
    changes: {
      from: 0,
      to: editorView.state.doc.length,
      insert: text,
    },
  });
}

async function loadGraphGallery() {
  try {
    const response = await fetch('/data/graphs.json');
    const data = await response.json();

    const selector = document.getElementById('graph-selector');
    selector.innerHTML = '<option value="">Select a graph...</option>';

    data.graphs.forEach(graph => {
      const option = document.createElement('option');
      option.value = graph.file;
      option.textContent = graph.title;
      option.dataset.description = graph.description;
      selector.appendChild(option);
    });

    // Set up change listener
    selector.addEventListener('change', handleGraphSelection);

    // Check URL hash for graph to load
    const urlHash = window.location.hash.substring(1); // Remove '#'
    const urlParams = new URLSearchParams(urlHash);
    const graphParam = urlParams.get('graph');

    if (graphParam) {
      // Try to load graph from URL parameter (graphParam never has .json)
      const matchingGraph = data.graphs.find(g => g.file === graphParam);
      if (matchingGraph) {
        selector.value = graphParam;
        await loadGraphFromFile(graphParam, matchingGraph.description);
      } else {
        // Graph not found, load default
        console.warn(`Graph "${graphParam}" not found in gallery`);
        if (data.graphs.length > 0) {
          selector.value = data.graphs[0].file;
          await loadGraphFromFile(data.graphs[0].file, data.graphs[0].description);
        }
      }
    } else {
      // No URL parameter, load first graph by default
      if (data.graphs.length > 0) {
        selector.value = data.graphs[0].file;
        await loadGraphFromFile(data.graphs[0].file, data.graphs[0].description);
        // Set initial URL hash
        updateURLHash(data.graphs[0].file);
      }
    }
  } catch (error) {
    console.error('Failed to load graph gallery:', error);
    showViewerStatus('Failed to load graph gallery', 'error');
  }
}

async function handleGraphSelection(event) {
  const file = event.target.value;
  if (!file) return;

  const selectedOption = event.target.options[event.target.selectedIndex];
  const description = selectedOption.dataset.description;

  await loadGraphFromFile(file, description);

  // Update URL hash when user selects a graph
  updateURLHash(file);
}

async function loadGraphFromFile(filename, description) {
  try {
    showViewerStatus('Loading graph...', 'loading');

    // Auto-append .json if not present
    const fullFilename = filename.endsWith('.json') ? filename : `${filename}.json`;

    const response = await fetch(`/data/${fullFilename}`);
    const graphData = await response.json();

    // Store for export
    originalBackendData = JSON.parse(JSON.stringify(graphData));

    // Display source YAML in editor
    if (graphData.source) {
      setEditorText(graphData.source);
    } else {
      setEditorText(`# ${graphData.title}\n\n# Graph data loaded from JSON\n# ${Object.keys(graphData.nodes || []).length} nodes, ${Object.keys(graphData.edges || []).length} edges`);
    }

    // Show description
    showGraphInfo(description);

    // Check if hierarchical
    if (graphData.children) {
      navigationManager.setData(graphData);
      const currentGraph = navigationManager.getCurrentGraph();
      graphRenderer.render(currentGraph);
    } else {
      // Clear navigation manager for flat graphs
      navigationManager.hierarchyData = null;
      navigationManager.currentPath = [];
      currentGraphData = graphData;
      graphRenderer.render(graphData);
    }

    // Always update breadcrumb visibility (shows for hierarchical, hides for flat)
    updateBreadcrumbVisibility();

    clearViewerStatus();
  } catch (error) {
    console.error('Failed to load graph:', error);
    showViewerStatus(`Failed to load graph: ${error.message}`, 'error');
  }
}

function updateURLHash(graphFile) {
  // Update URL hash without triggering page reload
  const newHash = `#graph=${encodeURIComponent(graphFile)}`;
  history.replaceState(null, '', newHash);
}

async function handleHashChange() {
  // Handle browser back/forward button navigation
  const urlHash = window.location.hash.substring(1);
  const urlParams = new URLSearchParams(urlHash);
  const graphParam = urlParams.get('graph');

  if (!graphParam) return;

  const selector = document.getElementById('graph-selector');
  const selectedOption = Array.from(selector.options).find(opt => opt.value === graphParam);

  if (selectedOption) {
    // Update selector without triggering change event (to avoid infinite loop)
    selector.value = graphParam;
    const description = selectedOption.dataset.description;
    await loadGraphFromFile(graphParam, description);
  }
}

function showGraphInfo(description) {
  const infoDiv = document.getElementById('graph-info');
  const descriptionP = document.getElementById('graph-description');

  if (description) {
    descriptionP.textContent = description;
    infoDiv.classList.remove('hidden');
  } else {
    infoDiv.classList.add('hidden');
  }
}

function showViewerStatus(message, type = 'loading') {
  const statusEl = document.getElementById('viewer-status');
  statusEl.textContent = message;
  statusEl.className = type;
}

function clearViewerStatus() {
  const statusEl = document.getElementById('viewer-status');
  statusEl.textContent = '';
  statusEl.className = 'hidden';
}

function setupEventListeners() {
  // Hamburger menu button (mobile)
  document.getElementById("hamburger-btn").addEventListener("click", () => {
    const app = document.getElementById("app");
    app.classList.toggle("mobile-menu-open");
  });

  // Close mobile menu when clicking overlay
  document.getElementById("app").addEventListener("click", (e) => {
    const app = document.getElementById("app");
    if (app.classList.contains("mobile-menu-open") && e.target === app) {
      app.classList.remove("mobile-menu-open");
    }
  });

  // Close mobile menu when selecting a graph
  document.getElementById('graph-selector').addEventListener('change', () => {
    const app = document.getElementById("app");
    app.classList.remove("mobile-menu-open");
  });

  // Navigate up button
  document.getElementById("nav-up-btn").addEventListener("click", () => {
    if (navigationManager) {
      navigationManager.navigateUp();
    }
  });

  // Reset view button
  document.getElementById("reset-view-btn").addEventListener("click", () => {
    if (graphRenderer) {
      graphRenderer.resetView();
    }
  });

  // Spacing slider
  document.getElementById("spacing-slider").addEventListener("input", (e) => {
    if (graphRenderer) {
      const spacing = parseFloat(e.target.value);
      graphRenderer.setSpacing(spacing);
    }
  });

  // Toggle edge emojis button
  document
    .getElementById("toggle-edge-emojis-btn")
    .addEventListener("click", () => {
      if (graphRenderer) {
        const newState = !graphRenderer.showEdgeEmojis;
        graphRenderer.toggleEdgeEmojis(newState);
        const btn = document.getElementById("toggle-edge-emojis-btn");
        btn.style.background = newState ? "var(--accent)" : "";
      }
    });

  // Toggle legend button
  document.getElementById("toggle-legend-btn").addEventListener("click", () => {
    const legend = document.getElementById("relationship-legend");
    const isHidden = legend.classList.contains("hidden");
    legend.classList.toggle("hidden");
    const btn = document.getElementById("toggle-legend-btn");
    btn.style.background = isHidden ? "var(--accent)" : "";
  });

  // Toggle minimap button
  document
    .getElementById("toggle-minimap-btn")
    .addEventListener("click", () => {
      const minimap = document.getElementById("minimap-container");
      const isHidden = minimap.classList.contains("hidden");

      if (isHidden && navigationManager && navigationManager.hierarchyData) {
        renderMinimap(
          navigationManager.hierarchyData,
          navigationManager.currentPath,
          (level) => {
            navigationManager.navigateToLevel(level);
          },
        );
        minimap.classList.remove("hidden");
        const btn = document.getElementById("toggle-minimap-btn");
        btn.style.background = "var(--accent)";
      } else if (!isHidden) {
        minimap.classList.add("hidden");
        const btn = document.getElementById("toggle-minimap-btn");
        btn.style.background = "";
      } else {
        showViewerStatus("Minimap only available for hierarchical data", "error");
        setTimeout(clearViewerStatus, 2000);
      }
    });

  // Close minimap button
  document.getElementById("close-minimap-btn").addEventListener("click", () => {
    const minimap = document.getElementById("minimap-container");
    minimap.classList.add("hidden");
    const btn = document.getElementById("toggle-minimap-btn");
    btn.style.background = "";
  });

  // Export button
  document.getElementById("export-json-btn").addEventListener("click", () => {
    if (originalBackendData) {
      const timestamp = new Date().toISOString().split(".")[0];
      exportJSON(originalBackendData, `emapi-${timestamp}.json`);
      showViewerStatus("Graph exported successfully!", "success");
      setTimeout(clearViewerStatus, 3000);
    } else {
      showViewerStatus("No graph to export", "error");
      setTimeout(clearViewerStatus, 3000);
    }
  });

  // Import button
  document.getElementById("import-json-btn").addEventListener("click", () => {
    document.getElementById("import-json-input").click();
  });

  document
    .getElementById("import-json-input")
    .addEventListener("change", async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        showViewerStatus("Importing graph...", "loading");
        const data = await importJSON(file);

        if (data.source) {
          setEditorText(data.source);
        }

        const { source, ...graphData } = data;
        originalBackendData = JSON.parse(JSON.stringify(graphData));

        if (graphData.children) {
          navigationManager.setData(graphData);
          const currentGraph = navigationManager.getCurrentGraph();
          graphRenderer.render(currentGraph);
        } else {
          // Clear navigation manager for flat graphs
          navigationManager.hierarchyData = null;
          navigationManager.currentPath = [];
          currentGraphData = graphData;
          graphRenderer.render(graphData);
        }

        // Always update breadcrumb visibility (shows for hierarchical, hides for flat)
        updateBreadcrumbVisibility();

        showViewerStatus("Graph imported successfully!", "success");
        setTimeout(clearViewerStatus, 3000);
      } catch (error) {
        showViewerStatus(`Import failed: ${error.message}`, "error");
        setTimeout(clearViewerStatus, 5000);
      }

      e.target.value = "";
    });

  // Click background to hide node details
  document.getElementById("graph-svg").addEventListener("click", () => {
    hideNodeDetails();
  });
}

function handleNodeClick(node) {
  showNodeDetails(node);
}

function handleNodeDrillDown(node) {
  if (!navigationManager || !navigationManager.hierarchyData) return;

  const success = navigationManager.drillDown(node.id);

  if (!success) {
    showViewerStatus("Cannot navigate to this node", "error");
    setTimeout(clearViewerStatus, 2000);
  }
}

function handleNavigation(path, graphData) {
  graphRenderer.render(graphData);

  const breadcrumbs = navigationManager.getBreadcrumbs();
  renderBreadcrumbs(breadcrumbs, (level) => {
    navigationManager.navigateToLevel(level);
  });

  const minimap = document.getElementById("minimap-container");
  if (!minimap.classList.contains("hidden")) {
    renderMinimap(
      navigationManager.hierarchyData,
      navigationManager.currentPath,
      (level) => {
        navigationManager.navigateToLevel(level);
      },
    );
  }

  const navUpBtn = document.getElementById("nav-up-btn");
  navUpBtn.disabled = navigationManager.isAtRoot();

  // Highlight corresponding text in editor
  highlightNavigationPath(editorView, path);

  hideNodeDetails();
}

function updateBreadcrumbVisibility() {
  const breadcrumbContainer = document.getElementById("breadcrumb-container");

  if (navigationManager && navigationManager.hierarchyData) {
    breadcrumbContainer.classList.remove("hidden");

    const breadcrumbs = navigationManager.getBreadcrumbs();
    renderBreadcrumbs(breadcrumbs, (level) => {
      navigationManager.navigateToLevel(level);
    });

    const navUpBtn = document.getElementById("nav-up-btn");
    navUpBtn.disabled = navigationManager.isAtRoot();
  } else {
    breadcrumbContainer.classList.add("hidden");
  }
}

// Start the app
init();
